import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { getLenis } from '../motion/SmoothScroll';
import { projectMedia } from '../data/site';

const ease = [0.22, 1, 0.36, 1];
const STATUS_CLASS = { Completed: 'badge-success', Ongoing: 'badge-warning', Upcoming: 'badge-blue' };

/* Full-screen project viewer: photo slider (arrows, swipe, keyboard, thumbnails) + project details */
export default function ProjectLightbox({ project, onClose }) {
  const media = projectMedia(project);
  const [[index, dir], setIndex] = useState([0, 0]);
  const total = media?.gallery.length || 0;

  const go = useCallback((d) => setIndex(([i]) => [(i + d + total) % total, d]), [total]);

  useEffect(() => { setIndex([0, 0]); }, [project]);
  useEffect(() => {
    if (!project) return undefined;
    getLenis()?.stop();
    document.documentElement.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); getLenis()?.start(); document.documentElement.style.overflow = ''; };
  }, [project, onClose, go]);

  return (
    <AnimatePresence>
      {project && media && (
        <motion.div className="lb" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.35 } }} onClick={onClose}>
          <motion.div className="lb-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={project.title}
            initial={{ y: 60, scale: 0.94, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} exit={{ y: 40, scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.6, ease }}>
            <div className="lb-stage">
              <AnimatePresence initial={false} custom={dir} mode="popLayout">
                <motion.img key={index} src={media.gallery[index].src} alt={`${project.title} — photo ${index + 1}`} className="lb-img"
                  custom={dir}
                  variants={{ enter: (d) => ({ x: d >= 0 ? '30%' : '-30%', opacity: 0, scale: 1.08 }), center: { x: 0, opacity: 1, scale: 1 }, exit: (d) => ({ x: d >= 0 ? '-20%' : '20%', opacity: 0, scale: 0.98 }) }}
                  initial="enter" animate="center" exit="exit" transition={{ duration: 0.7, ease }}
                  drag={total > 1 ? 'x' : false} dragConstraints={{ left: 0, right: 0 }} dragElastic={0.25}
                  onDragEnd={(_, info) => { if (info.offset.x < -80) go(1); else if (info.offset.x > 80) go(-1); }} />
              </AnimatePresence>
              {total > 1 && (
                <>
                  <button className="lb-nav prev" onClick={() => go(-1)} aria-label="Previous photo"><Icon name="arrow" size={20} style={{ transform: 'rotate(180deg)' }} /></button>
                  <button className="lb-nav next" onClick={() => go(1)} aria-label="Next photo"><Icon name="arrow" size={20} /></button>
                  <span className="lb-count">{String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
                </>
              )}
            </div>

            <div className="lb-info">
              <button className="lb-close" onClick={onClose} aria-label="Close"><Icon name="close" size={20} /></button>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6, ease }}>
                {project.status && <span className={`badge ${STATUS_CLASS[project.status] || 'badge-blue'}`}>{project.status}</span>}
                <span className="lb-cat">{project.category}</span>
                <h2 className="lb-title">{project.title}</h2>
                <dl className="lb-meta">
                  {project.location && <><dt>Location</dt><dd><Icon name="pin" size={15} />{project.location}</dd></>}
                  {project.scope && <><dt>PMC scope</dt><dd>{project.scope}</dd></>}
                  {project.specs && <><dt>Specs</dt><dd>{project.specs}</dd></>}
                </dl>
                {project.description && <p className="lb-desc">{project.description}</p>}
                <Link to="/request-consultation" className="btn btn-primary lb-cta" onClick={onClose}>Discuss a similar project <span className="arrow"><Icon name="arrow" size={14} /></span></Link>
                {total > 1 && (
                  <div className="lb-thumbs">
                    {media.gallery.map((g, i) => (
                      <button key={g.src} className={i === index ? 'on' : ''} onClick={() => setIndex([i, i > index ? 1 : -1])} aria-label={`Photo ${i + 1}`}>
                        <img src={g.sm} alt="" />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
